// src/containers/preview/DemoLeaderboard.js
//
// The weekly class leaderboard with the made-up class — drawn with the real
// leaderboard's styles (components/leaderboard/leaderboard.css). Coins the
// visitor earns in the demo put "You" on the board, wearing the avatar they
// decorated, so they can watch themselves climb.

import React, { useEffect, useMemo, useState } from "react";
import ModalPortal from "../../components/common/ModalPortal";
import AvatarWithOutfit from "../../components/avatar/AvatarWithOutfit";
import { Coin } from "../../components/coins/CoinCounter";
import { DEMO_TEACHER, demoBoard, weekResetLabel } from "./demoContent";
import "../../components/leaderboard/leaderboard.css";

const MEDALS = ["🥇", "🥈", "🥉"];
const NO_ITEMS = {};

// All-schools board: class totals only, as the real one shows.
const SCHOOLS = [
  { id: "s1", name: "Oak Hill Owls", state: "VA", grade: "3rd", coins: 4120 },
  { id: "s2", name: DEMO_TEACHER.room, state: "CA", grade: "3rd", coins: 3766, mine: true },
  { id: "s3", name: "Maple Stars", state: "NC", grade: "3rd", coins: 3398 },
  { id: "s4", name: "Lakeside Lions", state: "TX", grade: "3rd", coins: 2951 },
  { id: "s5", name: "Room 7 Comets", state: "NY", grade: "3rd", coins: 2410 },
  { id: "s6", name: "Sunrise Sharks", state: "GA", grade: "3rd", coins: 1887 },
].map((r, i) => ({ ...r, place: i + 1 }));

const Face = ({ row, myLook, size }) => <AvatarWithOutfit look={row.me ? myLook : row.look} size={size} items={NO_ITEMS} />;

const DemoLeaderboard = ({ myCoins = 0, myLook, onClose, onSignup }) => {
  const [tab, setTab] = useState("class");
  const rows = useMemo(() => demoBoard(myCoins), [myCoins]);
  const me = rows.find((r) => r.me);
  const ahead = me ? rows.filter((r) => r.coins > me.coins).pop() : null;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const podium = (list, withFaces) => (
    <div className="sbl-lb-podium">
      {[list[1], list[0], list[2]].map((r, i) => {
        if (!r) return <div key={i} className="sbl-lb-spot is-empty" />;
        const step = i === 1 ? 1 : i === 0 ? 2 : 3;
        return (
          <div key={r.id} className={`sbl-lb-spot is-${step} ${r.me || r.mine ? "is-me" : ""}`}>
            <div className="sbl-lb-spot-who">
              {withFaces ? <Face row={r} myLook={myLook} size={step === 1 ? 92 : 74} /> : <span className="sbl-lb-team-badge">{r.state}</span>}
              <span className="sbl-lb-medal">{MEDALS[r.place - 1] || r.place}</span>
            </div>
            <strong className="sbl-lb-spot-name">{r.me ? "You!" : r.name}</strong>
            <span className="sbl-lb-coins">
              <Coin size={16} /> {r.coins}
            </span>
            <div className="sbl-lb-step">{r.place}</div>
          </div>
        );
      })}
    </div>
  );

  const list = (items, withFaces) => (
    <ol className="sbl-lb-rows">
      {items.map((r) => (
        <li key={r.id} className={r.me || r.mine ? "is-me" : ""}>
          <span className="sbl-lb-place">{r.place}</span>
          {withFaces ? <Face row={r} myLook={myLook} size={38} /> : <span className="sbl-lb-team-badge is-small">{r.state}</span>}
          <span className="sbl-lb-name">
            {r.me ? "You" : r.name}
            {r.mine ? " ⭐ (this class)" : ""}
            {!withFaces && r.grade ? <small> · {r.grade}</small> : null}
          </span>
          <span className="sbl-lb-coins">
            <Coin size={15} /> {r.coins}
          </span>
        </li>
      ))}
    </ol>
  );

  return (
    <ModalPortal>
      <div className="sbl-lb-pop pvd-lb" role="dialog" aria-modal="true" aria-label="Leaderboard" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
        <div className="sbl-lb">
          <header className="sbl-lb-head">
            <h2>🏆 Leaderboard</h2>
            <span className="sbl-lb-reset" title="Everyone starts again at 0 on Monday">
              ⏳ New week in {weekResetLabel()}
            </span>
            <button type="button" className="sbl-lb-x" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </header>

          <nav className="sbl-lb-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === "class"} className={tab === "class" ? "is-on" : ""} onClick={() => setTab("class")}>
              🏫 My class
            </button>
            <button type="button" role="tab" aria-selected={tab === "league"} className={tab === "league" ? "is-on" : ""} onClick={() => setTab("league")}>
              🌎 All schools
            </button>
          </nav>

          <div className="sbl-lb-body">
            <p className="pvd-banner is-tight">Sample class — made-up students. Earn coins in the demo games, videos and songs to get on the board!</p>
            {tab === "class" ? (
              <>
                {podium(rows, true)}
                {rows.length > 3 && list(rows.slice(3), true)}
                <p className="sbl-lb-me">
                  {me ? (
                    <>
                      You're <b>#{me.place}</b> with <Coin size={15} /> <b>{me.coins}</b> this week.
                      {ahead && ` ${ahead.coins - me.coins + 1} more to move up!`}
                    </>
                  ) : (
                    "Play a demo game or watch a video to get on the board!"
                  )}
                </p>
                <div className="sbl-lb-champ">
                  <AvatarWithOutfit look={rows.find((r) => !r.me)?.look} size={44} items={NO_ITEMS} />
                  <span>
                    Last week's champion: <b>{rows.find((r) => !r.me)?.name}</b> with 388 coins
                  </span>
                  <span className="sbl-lb-crown" aria-hidden="true">
                    👑
                  </span>
                </div>
              </>
            ) : (
              <>
                {podium(SCHOOLS, false)}
                {list(SCHOOLS.slice(3), false)}
                <p className="sbl-lb-me">Teachers choose whether their class joins the all-schools board. Only a team name, state, grade and class total are shown.</p>
              </>
            )}
            <div className="pvd-box-foot">
              <button type="button" className="pvd-cta" onClick={onSignup}>
                ✨ Start my free trial
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default DemoLeaderboard;
